/**
 * Termine für fremde Kalender aufbereiten.
 *
 * Diese Datei wird sowohl im Browser (Google- und Outlook-Links) als auch auf
 * dem Server (ICS-Datei) verwendet. Sie darf deshalb nichts ausser Bordmitteln
 * benutzen – kein React, kein DOM.
 *
 * Die Anlässe sind reine Ortszeit ohne Zeitzone. Massgebend ist Europe/Zurich.
 */

export const TIMEZONE = "Europe/Zurich";

/** Standarddauer, wenn nur eine Anfangszeit erfasst ist. */
const DEFAULT_MINUTES = 120;

const toMinutes = (t) => {
  const [h, m] = String(t).split(":").map(Number);
  return h * 60 + m;
};

const pad = (n) => String(n).padStart(2, "0");

const addDays = (iso, days) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

/**
 * Anfang und Ende eines Anlasses als Ortszeit.
 * Ohne Anfangszeit wird daraus ein ganztägiger Eintrag.
 * Eine Endzeit vor der Anfangszeit gilt als am Folgetag.
 */
export function eventRange(ev) {
  if (!ev || !/^\d{4}-\d{2}-\d{2}$/.test(String(ev.date || ""))) return null;

  if (!ev.timeFrom) {
    return {
      allDay: true,
      startDate: ev.date,
      // Bei ganztägigen Einträgen ist das Ende laut Norm der Folgetag.
      endDate: addDays(ev.date, 1),
    };
  }

  const start = toMinutes(ev.timeFrom);
  let end = ev.timeTo ? toMinutes(ev.timeTo) : start + DEFAULT_MINUTES;
  if (end <= start) end += 24 * 60;

  return {
    allDay: false,
    startDate: ev.date,
    startTime: `${pad(Math.floor(start / 60) % 24)}:${pad(start % 60)}`,
    endDate: addDays(ev.date, Math.floor(end / (24 * 60))),
    endTime: `${pad(Math.floor(end / 60) % 24)}:${pad(end % 60)}`,
  };
}

/* ------------------------------------------------------------------ */
/*  Zeitzone                                                          */
/* ------------------------------------------------------------------ */

const zurichParts = new Intl.DateTimeFormat("en-US", {
  timeZone: TIMEZONE,
  hour12: false,
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
});

/** Wie weit liegt Zürich zu diesem Zeitpunkt vor UTC (in Millisekunden)? */
function offsetAt(timestamp) {
  const p = Object.fromEntries(
    zurichParts.formatToParts(timestamp).map((x) => [x.type, x.value])
  );
  const asUtc = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(p.hour) % 24, Number(p.minute), Number(p.second)
  );
  return asUtc - timestamp;
}

/** Zeitverschiebung für eine Ortszeit, z. B. "+02:00". */
export function zurichOffset(dateIso, time = "12:00") {
  const [y, m, d] = dateIso.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const naive = Date.UTC(y, m - 1, d, hh, mm);

  // Zweiter Durchgang, damit auch die beiden Umstellungstage stimmen.
  let off = offsetAt(naive);
  off = offsetAt(naive - off);

  const minutes = Math.round(off / 60000);
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/* ------------------------------------------------------------------ */
/*  Beschreibungstext                                                 */
/* ------------------------------------------------------------------ */

export function calendarDetails(ev, siteUrl) {
  return [
    ev.description || "",
    ev.host ? `Gastgeber: ${ev.host}` : "",
    ev.link ? `Anmeldung: ${ev.link}` : "",
    siteUrl ? `Alle Anlässe: ${siteUrl}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");
}

/* ------------------------------------------------------------------ */
/*  Links in fremde Kalender                                          */
/* ------------------------------------------------------------------ */

const compact = (date, time) =>
  time ? `${date.replace(/-/g, "")}T${time.replace(":", "")}00` : date.replace(/-/g, "");

/** Google Kalender: Ortszeit plus ausdrückliche Zeitzone. */
export function googleCalendarUrl(ev, siteUrl) {
  const r = eventRange(ev);
  if (!r) return "";
  const dates = r.allDay
    ? `${compact(r.startDate)}/${compact(r.endDate)}`
    : `${compact(r.startDate, r.startTime)}/${compact(r.endDate, r.endTime)}`;

  const p = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.title || "Anlass",
    dates,
    ctz: TIMEZONE,
  });
  const details = calendarDetails(ev, siteUrl);
  if (details) p.set("details", details);
  if (ev.location) p.set("location", ev.location);

  return `https://calendar.google.com/calendar/render?${p}`;
}

/**
 * Outlook im Browser. "work" trifft Microsoft 365, "personal" Outlook.com –
 * die beiden Dienste haben getrennte Adressen.
 */
export function outlookCalendarUrl(ev, kind = "work", siteUrl) {
  const r = eventRange(ev);
  if (!r) return "";

  const p = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: ev.title || "Anlass",
  });

  if (r.allDay) {
    p.set("allday", "true");
    p.set("startdt", r.startDate);
    p.set("enddt", r.endDate);
  } else {
    // Mit Zeitverschiebung, damit der Termin auch im Ausland richtig landet.
    p.set("startdt", `${r.startDate}T${r.startTime}:00${zurichOffset(r.startDate, r.startTime)}`);
    p.set("enddt", `${r.endDate}T${r.endTime}:00${zurichOffset(r.endDate, r.endTime)}`);
  }

  const details = calendarDetails(ev, siteUrl);
  if (details) p.set("body", details);
  if (ev.location) p.set("location", ev.location);

  const host = kind === "personal" ? "outlook.live.com" : "outlook.office.com";
  return `https://${host}/calendar/0/deeplink/compose?${p}`;
}

/** Adresse der Kalenderdatei auf diesem Server. */
export const icsUrl = (ev) => `/api/events/${encodeURIComponent(ev.id)}/kalender.ics`;
